import { useMemo, useState } from "react";
import { useQuery } from "convex/react";
import { toolDisplayName } from "@agentguard/shared-lib/aiTools";
import { PageHeader } from "../PageHeader";
import { MetricCard } from "../shared/MetricCard";
import { FilterChips } from "../shared/FilterChips";
import { SectionBoundary } from "../shared/SectionBoundary";
import { DailyStackChart, type DayValues } from "../shared/DailyStackChart";
import { usePeople } from "../aiUsage/usePeople";
import {
  dayRange,
  formatCount,
  formatPercent,
  formatSpend,
  formatUsd,
  projectLabel,
  spendParts,
} from "../aiUsage/format";
import type { SessionView } from "../aiUsage/sessions";
import { useNow } from "../aiUsage/useNow";
import { api as convexApi } from "../../../lib/convexApi";

type Period = "7" | "30" | "mtd";
type GroupBy = "tool" | "model" | "person";
// A breakdown row or a day's group: its cost, its calls, and how many of
// those calls Convex could not price (they add nothing to the cost).
type Row = { key: string; costMicroUsd: number; calls: number; unpricedCalls: number };
type Counts = { calls: number; unpricedCalls: number };

// convex/observability/finops.ts `summary`. Every stored key (a tool, model,
// repo or user id) is a value in an array, never an object key. An unknown
// model or repo is "".
interface Spend {
  scope: "org" | "self";
  truncated: boolean;
  totals: {
    costMicroUsd: number;
    calls: number;
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
    unpricedCalls: number;
  };
  cacheSavingsMicroUsd: number;
  cacheHitRate: number;
  unpricedShare: number;
  sessions: number;
  // The sessions in `sessions` that made at least one call.
  sessionsWithCalls: number;
  daily: Array<{ day: string; groups: Row[] }>;
  groupKeys: string[];
  byPerson: Row[];
  byRepo: Row[];
  byModel: Row[];
  // The ten dearest sessions last seen in the range that cost something, each
  // with its whole cost.
  topSessions: SessionView[];
}

const PERIOD_LABEL: Record<Period, string> = {
  "7": "last 7 days",
  "30": "last 30 days",
  mtd: "month to date",
};

// Convex's key for the rest of a capped list (convex/observability/lib.ts
// OTHER): a person's usage past their daily row cap, folded by tool, model and
// repo. It is not a tool, so it never goes to a tool's name lookup.
const FOLDED = "~other";
const OTHER_LABEL = "Other";
// The most rows finops.summary returns per breakdown (BREAKDOWN_ROWS).
const BREAKDOWN_ROWS = 20;
const DAY_MS = 86_400_000;

const plural = (n: number, one: string, many: string) =>
  `${formatCount(n)} ${n === 1 ? one : many}`;

// Calls and unpriced calls summed per key. A Map: the keys come from the
// server, and a model may be named "constructor".
function add(map: Map<string, Counts>, key: string, row: Counts) {
  const cur = map.get(key) ?? { calls: 0, unpricedCalls: 0 };
  map.set(key, {
    calls: cur.calls + row.calls,
    unpricedCalls: cur.unpricedCalls + row.unpricedCalls,
  });
}

function modelLabel(key: string): string {
  return key === FOLDED ? OTHER_LABEL : key || "unknown model";
}

function projectKeyLabel(key: string): string {
  return key === FOLDED ? OTHER_LABEL : key || "unknown project";
}

// Every day of the range, oldest first, so a day without calls is drawn as an
// empty slot rather than left out. A day's groups become an object built with
// Object.fromEntries, whose keys are all own (even "__proto__"), and the chart
// reads only own keys, so a model named like an Object member stays itself.
function rangeDays(fromDay: string, toDay: string, daily: Spend["daily"]): DayValues[] {
  const byDay = new Map(
    daily.map((d) => [d.day, Object.fromEntries(d.groups.map((g) => [g.key, g.costMicroUsd]))])
  );
  const keys = new Set<string>();
  const from = Date.parse(`${fromDay}T00:00:00Z`);
  const to = Date.parse(`${toDay}T00:00:00Z`);
  for (let t = from; t <= to; t += DAY_MS) keys.add(new Date(t).toISOString().slice(0, 10));
  for (const d of daily) keys.add(d.day);
  return [...keys].sort().map((day) => ({ day, values: byDay.get(day) ?? {} }));
}

// This period's spend over the sessions last seen in it that made calls
// (finops sessionsWithCalls): a session the gateway did not route has none,
// and no cost. Their calls can fall partly before the period, so the delta
// says what is counted.
function perSessionSpend(t: Spend["totals"], withCalls: number): { value: string; delta?: string } {
  if (withCalls === 0) return { value: "no sessions with calls" };
  const counted = `${plural(withCalls, "session", "sessions")} with calls, last seen in this period`;
  if (t.calls > 0 && t.unpricedCalls >= t.calls) return { value: "not priced", delta: counted };
  return {
    value: formatUsd(t.costMicroUsd / withCalls),
    delta: t.unpricedCalls > 0 ? `${counted}, priced calls only` : counted,
  };
}

export function AiSpendPage({ onOpenSession }: { onOpenSession: (id: string) => void }) {
  const [period, setPeriod] = useState<Period>("30");
  const [groupBy, setGroupBy] = useState<GroupBy>("tool");
  return (
    <>
      <PageHeader
        title="AI spend"
        subtitle={PERIOD_LABEL[period]}
        actions={
          <FilterChips
            label="Period"
            value={period}
            onChange={setPeriod}
            options={[
              { value: "7", label: "7 days" },
              { value: "30", label: "30 days" },
              { value: "mtd", label: "Month to date" },
            ]}
          />
        }
      />
      <div className="sr-page-body">
        {/* Keyed by the query's arguments: after a failure, another period or
            grouping is a new query and tries again. */}
        <SectionBoundary key={`${period}:${groupBy}`} label="AI spend">
          <SpendBody
            period={period}
            groupBy={groupBy}
            onGroupBy={setGroupBy}
            onOpenSession={onOpenSession}
          />
        </SectionBoundary>
      </div>
    </>
  );
}

function SpendBody({
  period,
  groupBy,
  onGroupBy,
  onOpenSession,
}: {
  period: Period;
  groupBy: GroupBy;
  onGroupBy: (g: GroupBy) => void;
  onOpenSession: (id: string) => void;
}) {
  // The period is whole UTC days to today, so it moves on at UTC midnight
  // while the page stays open, also after sleep (useNow).
  const now = useNow((n) => (Math.floor(n / DAY_MS) + 1) * DAY_MS);
  const range = useMemo(() => dayRange(period, now), [period, now]);
  const data = useQuery((convexApi as any).observability.finops.summary, { ...range, groupBy }) as
    | Spend
    | undefined;
  const { nameFor } = usePeople();
  const chart = useMemo(() => {
    const daily = data?.daily ?? [];
    // Calls per series and per day, so the legend and a day's tooltip can say
    // how much of the spend was priced (format.ts spendParts).
    const bySeries = new Map<string, Counts>();
    const byDay = new Map<string, Counts>();
    for (const d of daily) {
      for (const g of d.groups) {
        add(bySeries, g.key, g);
        add(byDay, d.day, g);
      }
    }
    return {
      days: rangeDays(range.fromDay, range.toDay, daily),
      // The chart draws FOLDED as its own fold (foldKey): last, in the fold
      // colour, merged with any series past the fifth, under one name.
      series: data?.groupKeys ?? [],
      bySeries,
      byDay,
    };
  }, [data, range]);
  if (data === undefined) return <div className="sr-empty">Loading spend…</div>;

  // The org's view: admins get every person; a member gets only their own.
  const orgView = data.scope === "org";
  // The chart draws FOLDED itself (foldKey), so only real keys reach here.
  const seriesLabel = (key: string) => {
    switch (groupBy) {
      case "tool":
        return toolDisplayName(key);
      case "person":
        return nameFor(key);
      case "model":
        return modelLabel(key);
    }
  };
  const t = data.totals;
  const spend = spendParts(t.costMicroUsd, t.calls, t.unpricedCalls);
  const perSession = perSessionSpend(t, data.sessionsWithCalls);
  // A legend entry sums one series, or every series in the fold.
  const legendValue = (keys: string[], total: number) => {
    const c = { calls: 0, unpricedCalls: 0 };
    for (const k of keys) {
      const x = chart.bySeries.get(k);
      if (x) {
        c.calls += x.calls;
        c.unpricedCalls += x.unpricedCalls;
      }
    }
    return formatSpend(total, c.calls, c.unpricedCalls);
  };

  return (
    <>
      {data.scope === "self" && <div className="sr-ai-scope">Showing your spend</div>}
      {t.calls === 0 ? (
        // Only an admin can change the gateway.
        <div className="sr-empty">
          {orgView
            ? "No model calls in this period. Switch the gateway to Observe in AI gateway, then open a Claude Code terminal."
            : "No model calls in this period. Ask an org admin to switch the gateway to Observe, then open a Claude Code terminal."}
        </div>
      ) : (
        <>
          <div className="sr-metric-grid">
            <MetricCard
              label="Spend"
              value={spend.amount}
              // A note on prices only under a dollar figure, not under "not
              // priced" (spendParts().priced), as on the AI overview.
              delta={
                !spend.priced
                  ? undefined
                  : spend.unpriced
                    ? `at API list prices; ${spend.unpriced}`
                    : "at API list prices"
              }
            />
            <MetricCard label="Model calls" value={formatCount(t.calls)} />
            <MetricCard label="Per session" value={perSession.value} delta={perSession.delta} />
            <MetricCard
              label="Cache hits"
              value={formatPercent(data.cacheHitRate)}
              // A lower bound: Convex counts no savings for a model it cannot
              // price or whose usage was folded.
              delta={
                data.cacheSavingsMicroUsd > 0
                  ? `saved at least ${formatUsd(data.cacheSavingsMicroUsd)}`
                  : undefined
              }
            />
          </div>

          <div className="sr-ai-toolbar" style={{ marginTop: 16 }}>
            <FilterChips
              label="Daily spend by"
              value={groupBy}
              onChange={onGroupBy}
              options={[
                { value: "tool", label: "Tool" },
                { value: "model", label: "Model" },
                ...(orgView ? [{ value: "person" as const, label: "Person" }] : []),
              ]}
            />
          </div>
          <div className="sr-card sr-card--chart">
            <DailyStackChart
              title="Daily spend"
              days={chart.days}
              series={chart.series}
              labelFor={seriesLabel}
              otherLabel={OTHER_LABEL}
              foldKey={FOLDED}
              formatValue={formatUsd}
              legendValue={legendValue}
              dayTitle={(day, total) => {
                const c = chart.byDay.get(day);
                return `${day}: ${c ? formatSpend(total, c.calls, c.unpricedCalls) : "no calls"}`;
              }}
              emptyText="No priced spend in this period"
            />
          </div>

          <div className="sr-ai-breakdowns">
            {orgView && data.byPerson.length > 0 && (
              <Breakdown title="By person" rows={data.byPerson} labelFor={nameFor} />
            )}
            <Breakdown title="By project" rows={data.byRepo} labelFor={projectKeyLabel} />
            <Breakdown title="By model" rows={data.byModel} labelFor={modelLabel} />
          </div>

          <h2 className="sr-section-title">Top sessions</h2>
          <div className="sr-ai-note">
            The dearest sessions last seen in this period, each with its whole cost.
          </div>
          {data.topSessions.length === 0 ? (
            <div className="sr-empty">No priced sessions last seen in this period.</div>
          ) : (
            <table className="sr-table">
              <thead>
                <tr>
                  <th>Session</th>
                  <th>Project</th>
                  <th className="is-num">Calls</th>
                  <th className="is-num">Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.topSessions.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <button type="button" className="sr-link" onClick={() => onOpenSession(s.id)}>
                        {nameFor(s.userId)}, {s.toolName}
                      </button>
                    </td>
                    <td>{projectLabel(s)}</td>
                    <td className="is-num">{formatCount(s.calls)}</td>
                    <td className="is-num">
                      {formatSpend(s.costMicroUsd, s.calls, s.unpricedCalls)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      <p className="sr-ai-note">
        Costs are API list prices. Subscription plans bill differently.
        {data.unpricedShare > 0
          ? ` ${formatPercent(data.unpricedShare)} of calls could not be priced.`
          : ""}
      </p>
      {data.truncated && (
        <p className="sr-ai-note">
          This period holds more than one summary can read: its oldest days may be missing, and the
          session count is a lower bound.
        </p>
      )}
    </>
  );
}

function Breakdown({
  title,
  rows,
  labelFor,
}: {
  title: string;
  rows: Row[];
  labelFor: (key: string) => string;
}) {
  return (
    <div className="sr-card">
      <h3 className="sr-section-title sr-section-title--flush">{title}</h3>
      <table className="sr-table">
        <tbody>
          {rows.map((r) => (
            // Keys are unique within a list; the prefix keeps "" a key.
            <tr key={`k:${r.key}`}>
              <td>{labelFor(r.key)}</td>
              <td className="is-num">{plural(r.calls, "call", "calls")}</td>
              <td className="is-num">{formatSpend(r.costMicroUsd, r.calls, r.unpricedCalls)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length >= BREAKDOWN_ROWS && (
        <div className="sr-ai-note">The {BREAKDOWN_ROWS} largest.</div>
      )}
    </div>
  );
}
